import React, { useEffect, useState } from 'react';
import { Box, Typography, Button, Divider, Alert } from '@mui/material';
import { warmPastels, pageGradient, containerBg } from '../styles/theme';
import QuizQuestion from './QuizQuestion';
import QuizScoreBox from './QuizScoreBox';
import QuizAutoButton from './QuizAutoButton';
import QuizPastQuizzes from './QuizPastQuizzes';
import QuizHomeButton from './QuizHomeButton';

const pastQuizzes = [
  { id: '2024-05-11', date: 'May 11, 2024' },
  { id: '2024-05-04', date: 'May 4, 2024' },
  { id: '2024-04-27', date: 'April 27, 2024' },
];

const apiUrl = process.env.REACT_APP_API_URL;
const apiKey = process.env.REACT_APP_API_KEY;

type Question = {
  question: string;
  answer: string;
};

const Quiz: React.FC = () => {
  const [questions, setQuestions] = useState<Question[]>([]);
  const [revealed, setRevealed] = useState<boolean[]>([]);
  const [markedCorrect, setMarkedCorrect] = useState<(boolean | null)[]>([]);
  const [showScore, setShowScore] = useState(false);
  const [showValidation, setShowValidation] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(apiUrl!, {
      headers: {
        'x-api-key': apiKey!,
        'content-type': 'application/json',
      },
    })
      .then(res => res.json())
      .then(data => {
        setQuestions(data.questions);
        setRevealed(Array(data.questions.length).fill(false));
        setMarkedCorrect(Array(data.questions.length).fill(null));
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  useEffect(() => {
    document.body.style.background = pageGradient;
    document.body.style.minHeight = "100vh";
    return () => {
      document.body.style.background = "";
      document.body.style.minHeight = "";
    };
  }, []);

  const handleReveal = (idx: number) => {
    setRevealed(prev => {
      const copy = [...prev];
      copy[idx] = !copy[idx];
      return copy;
    });
  };

  const handleMark = (idx: number, value: boolean) => {
    setMarkedCorrect(prev => {
      const copy = [...prev];
      copy[idx] = value;
      return copy;
    });
  };

  const handleFinish = () => {
    if (markedCorrect.some(v => v === null)) {
      setShowValidation(true);
      return;
    }
    setShowScore(true);
  };

  const handleAuto = () => {
    setMarkedCorrect(Array(questions.length).fill(true));
  };

  const score = markedCorrect.filter(v => v === true).length;
  const showAutoButton = process.env.REACT_APP_SHOW_AUTO_BUTTON === 'true';

  if (loading) return <div>Loading...</div>;

  if (showScore) {
    return (
      <Box
        maxWidth={600}
        mx="auto"
        p={4}
        sx={{
          background: containerBg,
          minHeight: '60vh',
          borderRadius: 6,
          boxShadow: 6,
          mt: 10,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          position: 'relative',
        }}
      >
        <QuizHomeButton />
        <Typography
          variant="h2"
          gutterBottom
          sx={{
            fontFamily: '"Baloo 2", Arial, sans-serif',
            fontWeight: 700,
            letterSpacing: 2,
            color: 'primary.main',
            mb: 3,
            textShadow: '1px 2px 8px #fffbe6',
          }}
        >
          Quiz Complete!
        </Typography>
        <Typography
          variant="h5"
          sx={{
            fontFamily: '"Baloo 2", Arial, sans-serif',
            fontWeight: 600,
            color: '#800020',
            mb: 2,
          }}
        >
          Your score: {score} out of {questions.length}
        </Typography>
        <Button
          variant="outlined"
          sx={{
            mt: 3,
            fontWeight: 700,
            fontFamily: '"Baloo 2", Arial, sans-serif',
            borderRadius: 4,
            px: 4,
            color: '#800020',
            borderColor: '#800020',
            '&:hover': {
              background: '#ffe5b4',
              borderColor: '#800020',
            },
          }}
          onClick={() => setShowScore(false)}
        >
          Return to Questions
        </Button>
        <QuizPastQuizzes pastQuizzes={pastQuizzes} />
      </Box>
    );
  }

  return (
    <Box
      maxWidth={600}
      mx="auto"
      p={3}
      sx={{
        background: containerBg,
        minHeight: '100vh',
        borderRadius: 6,
        boxShadow: 6,
        mt: 6,
        position: 'relative',
      }}
    >
      <QuizHomeButton />
      <Typography
        variant="h3"
        gutterBottom
        sx={{
          fontFamily: '"Baloo 2", Arial, sans-serif',
          fontWeight: 700,
          letterSpacing: 2,
          color: 'primary.main',
          textAlign: 'center',
          mb: 4,
          textShadow: '1px 2px 8px #fffbe6'
        }}
      >
        Quiz Time!
      </Typography>
      {questions.map((q, idx) => (
        <QuizQuestion
          key={idx}
          question={q.question}
          answer={q.answer}
          idx={idx}
          revealed={revealed[idx]}
          markedCorrect={markedCorrect[idx]}
          showValidation={showValidation}
          onReveal={handleReveal}
          onMark={handleMark}
          pastel={warmPastels[idx % warmPastels.length]}
        />
      ))}
      {showValidation && (
        <Alert severity={markedCorrect.every(v => v !== null) ? "success" : "warning"} sx={{ mb: 2 }}>
          {markedCorrect.every(v => v !== null)
            ? 'Much better'
            : "Ya didn't answer all the questions"}
        </Alert>
      )}
      <Divider sx={{ my: 2 }} />
      <Button
        variant="contained"
        color="primary"
        onClick={handleFinish}
        sx={{ fontWeight: 'bold', mt: 2 }}
      >
        Finish
      </Button>
      <QuizScoreBox score={score} total={questions.length} />
      {showAutoButton && <QuizAutoButton onAuto={handleAuto} />}
    </Box>
  );
};

export default Quiz;